import { useEffect, useState, useContext } from "react"
import { NavLink } from "react-router-dom"
import { sendEmail } from "../../../utils/mail"
import { AlertsContext } from "../../../utils/providers/alerts"
import * as mailTypes from "../../../utils/exports/mail-types"
import { db, auth, storage } from "../../../utils/firebase"
import firebase from "firebase"
import moment from "moment"

// UI components
import Button from "../../../components/ui/button/button"
import Badge from "../../../components/ui/badge/badge"
import Window from "../../../components/structure/window/window"
import WindowCard from "../../../components/ui/window-card/window-card"
import Input from "../../../components/ui/inputs/input"
import Datepicker from "../../../components/ui/datepicker/datepicker"
import Select from "../../../components/ui/select/select"
import { UploadIcon } from "../../../utils/svgs"
import { Eye, Trash2 } from "lucide-react"

// PDF generation
import fontkit from "@pdf-lib/fontkit"
import { PDFDocument } from "pdf-lib"
import { rgb } from "pdf-lib"

// Returns a table row for a voucher, with the ability to generate a PDF voucher
export default function VoucherRow({ id, purchased, purchased_by, purchased_for, issued_by, reference, value, status, bookingID, type, note, usersMap = {}, onUpdate, onDelete }) {
	// Get issued staff from pre-fetched usersMap
	const issuedStaff = usersMap[issued_by] || {}
	const [byClient, setByClient] = useState({})
	const [forClient, setForClient] = useState({})
	const [booking, setBooking] = useState({})
	const [document, setDocument] = useState({})
	const [generating, setGenerating] = useState(false)
	const [sending, setSending] = useState(false)

	// Payment modal state
	const [addingPayment, setAddingPayment] = useState(false)
	const [paymentAmount, setPaymentAmount] = useState("")
	const [paymentPayee, setPaymentPayee] = useState("")
	const [paymentReference, setPaymentReference] = useState("")
	const [paymentDate, setPaymentDate] = useState("")
	const [savingPayment, setSavingPayment] = useState(false)

	// View payments state
	const [viewingPayments, setViewingPayments] = useState(false)
	const [payments, setPayments] = useState([])

	// Edit details modal state
	const [editingDetails, setEditingDetails] = useState(false)
	const [editFirstName, setEditFirstName] = useState("")
	const [editLastName, setEditLastName] = useState("")
	const [editEmail, setEditEmail] = useState("")
	const [savingDetails, setSavingDetails] = useState(false)

	// Edit type modal state
	const [editingType, setEditingType] = useState(false)
	const [selectedType, setSelectedType] = useState(type || "")
	const [savingType, setSavingType] = useState(false)

	// Type labels mapping
	const typeLabels = {
		INT_COMPENSATION: "INT/Compensation",
		EXT_PARTNER: "EXT/Partner",
		EXT_MARKETING: "EXT/Marketing",
		EXT_PURCHASED: "EXT/Purchased",
	}

	// Delete state
	const [deleting, setDeleting] = useState(false)

	const purchasedString = moment(purchased.seconds, "X").format("MMM D[,] YYYY [at] HH:mm")
	const issuedDate = moment(purchased.seconds, "X").format("MMM D[,] YYYY")

	// Pull the alerts in for showing in the DOM
	const { pushAlert } = useContext(AlertsContext)

	const formattedPrice = new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency: "GBP",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(value)

	useEffect(() => {
		if (purchased_by) {
			db.doc(`clients/${purchased_by}`)
				.get()
				.then((clientDoc) => {
					if (clientDoc.exists) {
						setByClient(clientDoc.data())
					}
				})
		}

		if (purchased_for) {
			db.doc(`clients/${purchased_for}`)
				.get()
				.then((clientDoc) => {
					if (clientDoc.exists) {
						setForClient(clientDoc.data())
					}
				})
		}

		if (bookingID) {
			db.doc(`bookings/${bookingID}`)
				.get()
				.then((bookingDoc) => {
					if (bookingDoc.exists) {
						setBooking(bookingDoc.data())
					}
				})
		}

		db.doc(`gift_vouchers/${id}/documents/${reference}`)
			.get()
			.then((doc) => {
				if (doc.exists) {
					setDocument(doc.data())
				}
			})
	}, [])

	// Generate the voucher PDF that can be sent to the client
	const generateVoucher = async () => {
		// Toggle the loading state
		setGenerating(true)

		try {
			// Base URL for the original template PDF
			const baseTemplate =
				"https://firebasestorage.googleapis.com/v0/b/ghd-booking-system.appspot.com/o/_system%2Fdocuments%2Fvouchers%2FVoucher%20Template%20-%2002-12-24.pdf?alt=media&token=a84f729a-2281-4465-bacd-e5cc79451b10"

			// Fetch this template as an array buffer
			const templatePDFBytes = await fetch(baseTemplate).then((response) => response.arrayBuffer())

			// Load the document up within the pdf-ib library
			const quotePDFDocument = await PDFDocument.load(templatePDFBytes)

			// Register the font kit with the PDF library
			quotePDFDocument.registerFontkit(fontkit)

			// Download a buffer version of the poppins font
			const poppins600Bytes = await fetch(
				"https://firebasestorage.googleapis.com/v0/b/ghd-booking-system.appspot.com/o/_system%2Ffonts%2FPoppins-SemiBold.otf?alt=media&token=71d97025-3d30-4a6c-b662-4e7fdf051350"
			).then((res) => res.arrayBuffer())
			const poppins700Bytes = await fetch(
				"https://firebasestorage.googleapis.com/v0/b/ghd-booking-system.appspot.com/o/_system%2Ffonts%2FPoppins-Bold.otf?alt=media&token=f0a366b3-0378-4b9b-acc7-a6755ec44733"
			).then((res) => res.arrayBuffer())

			// Embed the different fonts into the PDF
			const poppins600 = await quotePDFDocument.embedFont(poppins600Bytes)
			const poppins700 = await quotePDFDocument.embedFont(poppins700Bytes)

			// Pull out the various pages on the PDF
			const voucherTemplate = quotePDFDocument.getPage(0)

			// Setup some height/width variables for the actual page size
			const { height } = voucherTemplate.getSize()

			// Calculate the full width of the text
			const valueArr = [{ text: `£${value}`, font: poppins700 }]
			const valueWidth = valueArr.reduce((acc, part) => acc + part.font.widthOfTextAtSize(part.text, 52), 0)
			const valueOffset = valueWidth / 2

			// Write this part onto the page
			quotePDFDocument.getPage(0).drawText(`£${value}`, {
				x: 83.25 - valueOffset,
				y: height - 109,
				size: 52,
				font: valueArr[0].font,
				color: rgb(0, 0, 0),
			})

			// Calculate the full width of the text
			const referenceArr = [{ text: reference, font: poppins600 }]
			const referenceWidth = referenceArr.reduce((acc, part) => acc + part.font.widthOfTextAtSize(part.text, 9.5), 0)
			const referenceOffset = referenceWidth / 2

			// Write this part onto the page
			quotePDFDocument.getPage(0).drawText(reference, {
				x: 83.25 - referenceOffset,
				y: height - 172,
				size: 9.5,
				font: referenceArr[0].font,
				color: rgb(0, 0, 0),
			})

			// Calculate the full width of the text
			const issuedArr = [{ text: reference, font: poppins600 }]
			const issuedWidth = issuedArr.reduce((acc, part) => acc + part.font.widthOfTextAtSize(`Issued ${issuedDate}`, 6.5), 0)
			const issuedOffset = issuedWidth / 2

			// Write this part onto the page
			quotePDFDocument.getPage(0).drawText(`Issued ${issuedDate}`, {
				x: 83.25 - issuedOffset,
				y: height - 182.5,
				size: 6.5,
				font: issuedArr[0].font,
				color: rgb(0, 0, 0),
			})

			// Save the new PDF document
			const newPDFBytes = await quotePDFDocument.save()

			// Then we want to upload this into storage
			await uploadPDFVoucher(newPDFBytes)
		} catch (err) {
			console.log("Error generating voucher", err)
		} finally {
			setGenerating(false)
		}
	}

	// Handle the actual upload process of the file into the storage bucket
	const handleDocumentUpload = async (documentUploadRef) => {
		// Attach a listener onto the upload job to stream the progress into state
		const monitorUploadProgress = (upload) => {
			return new Promise((resolve, reject) => {
				// Setup the listener on the state_change variable for the upload
				upload.on(
					"state_changed",
					(snapshot) => {
						// Calculate a percentage for the upload progress
						const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
						console.log(`Uploading voucher document...${progress}%`)
					},
					(error) => console.log(error),
					() => {
						resolve()
					}
				)
			})
		}

		// Return a promise to halt execution until the upload is complete
		return new Promise((resolve, reject) => {
			// Attach the monitor to the uploaded file
			monitorUploadProgress(documentUploadRef).then(() => {
				// When it's complete, get the downloadURL from the callback
				documentUploadRef.snapshot.ref.getDownloadURL().then(async (url) => {
					// Write this document into the gift_vouchers 'documents' subcollection
					await db.doc(`gift_vouchers/${id}/documents/${reference}`).set({
						pdf_url: url,
						created: firebase.firestore.FieldValue.serverTimestamp(),
					})

					// Resolve the promise
					resolve(url)
				})
			})
		})
	}

	// Upload the PDF quote into storage
	const uploadPDFVoucher = async (PDFBytes) => {
		// Establish some metadata for the document
		const meta = {
			contentType: "application/pdf",
			customMetadata: {
				fileID: reference,
				uploadedBy: auth.currentUser.uid,
			},
		}

		// Upload the document into storage
		const upload = storage.ref(`gift_vouchers/${id}/${reference}`).put(PDFBytes, meta)

		// Wait for the document to be uploaded into the storage bucket
		const uploadedDocumentURL = await handleDocumentUpload(upload)

		// Set the state and update the package in the quote context
		setGenerating(false)
		setDocument({ pdf_url: uploadedDocumentURL })

		// Close any loading states out
		return true
	}

	// Sends an email to the client with the voucher attached
	const sendVoucher = async () => {
		// Return early if the document is missing
		if (!document?.pdf_url) return

		// Loading state
		setSending(true)

		try {
			// Download the voucher PDF as bytes
			const voucherPDFBytes = await fetch(document?.pdf_url).then((response) => response.arrayBuffer())

			// Convert the unit array data into a base64 string
			const unitArrayToBase64 = async (unitArrayData) => {
				// Use a FileReader to generate a base64 data URI
				const base64url = await new Promise((r) => {
					const reader = new FileReader()
					reader.onload = () => r(reader.result)
					reader.readAsDataURL(new Blob([unitArrayData]))
				})

				// Complete result would show: "data:application/octet-stream;base64,<the data>", so just
				// return the remainder of the string behind the meta at the start
				return base64url.split(",", 2)[1]
			}

			// Write the new document into a base64 for embedding onto the email
			const documentAsBase64 = await unitArrayToBase64(voucherPDFBytes)

			// Send the client email
			await sendEmail({
				template: mailTypes.GIFT_VOUCHER.template,
				subject: `Golf Holidays Direct Gift Voucher - Ref: ${reference}`,
				from: {
					name: "Golf Holidays Direct",
					email: "sales@golfholidaysdirect.com",
				},
				user: {
					name: `${byClient.first_name} ${byClient.last_name}`,
					email: byClient.email,
				},
				mail: {
					name: `${byClient.first_name} ${byClient.last_name}`,
					value: `£${value}`,
				},
				attachments: [
					{
						type: "application/pdf",
						name: `Golf Holidays Direct Gift Voucher - ${reference}.pdf`,
						content: documentAsBase64,
					},
				],
			})

			// Write a log onto the new client account for the quote send
			await db.collection(`clients/${purchased_by}/logs`).add({
				badge: "INFO",
				type: "VOUCHER_SENT",
				user: auth.currentUser.uid,
				message: `The gift voucher PDF (${reference}) was sent to ${byClient.email}`,
				created: firebase.firestore.FieldValue.serverTimestamp(),
			})

			// Notify that the voucher has been sent
			pushAlert({
				type: "SUCCESS",
				title: "Voucher email sent to the buyer.",
			})
		} catch (err) {
			console.log("Error sending voucher", err)
		} finally {
			setSending(false)
		}
	}

	// Open edit details modal with current data
	const openEditDetails = () => {
		setEditFirstName(forClient.first_name || "")
		setEditLastName(forClient.last_name || "")
		setEditEmail(forClient.email || "")
		setEditingDetails(true)
	}

	// Update voucher details and link to client
	const updateVoucherDetails = async () => {
		setSavingDetails(true)

		try {
			// Search for existing client by email
			const clientQuery = await db.collection("clients").where("email", "==", editEmail.toLowerCase()).get()

			let clientID

			if (clientQuery.size > 0) {
				// Use existing client
				clientID = clientQuery.docs[0].id
			} else {
				// Create new client account
				const newClientDoc = await db.collection("clients").add({
					first_name: editFirstName.charAt(0).toUpperCase() + editFirstName.slice(1),
					last_name: editLastName.charAt(0).toUpperCase() + editLastName.slice(1),
					email: editEmail.toLowerCase(),
					created: firebase.firestore.FieldValue.serverTimestamp(),
				})

				clientID = newClientDoc.id

				// Add a log for the client account creation
				await db.collection(`clients/${clientID}/logs`).add({
					type: "CLIENT_CREATED",
					message: "Client account was created via voucher details update",
					created: firebase.firestore.FieldValue.serverTimestamp(),
				})
			}

			// Update the voucher with the new client reference
			await db.doc(`gift_vouchers/${id}`).update({
				purchased_for: clientID,
			})

			// Update local state
			setForClient({
				first_name: editFirstName.charAt(0).toUpperCase() + editFirstName.slice(1),
				last_name: editLastName.charAt(0).toUpperCase() + editLastName.slice(1),
				email: editEmail.toLowerCase(),
			})

			pushAlert({
				type: "SUCCESS",
				title: "Voucher details updated successfully.",
			})

			setEditingDetails(false)
		} catch (err) {
			console.error("Error updating voucher details:", err)
			pushAlert({
				type: "ERROR",
				title: "Error updating voucher details.",
			})
		} finally {
			setSavingDetails(false)
		}
	}

	// Save payment to the voucher's payments subcollection
	const savePayment = async () => {
		setSavingPayment(true)

		try {
			const amountVal = Number.parseFloat(paymentAmount)

			await db.collection(`gift_vouchers/${id}/payments`).add({
				amount: amountVal,
				payee: paymentPayee,
				reference: paymentReference,
				paid_date: firebase.firestore.Timestamp.fromMillis(paymentDate),
				created: firebase.firestore.FieldValue.serverTimestamp(),
				saved_by: auth.currentUser.uid,
			})

			pushAlert({
				type: "SUCCESS",
				title: "Payment saved successfully.",
			})

			// Reset and close
			setPaymentAmount("")
			setPaymentPayee("")
			setPaymentReference("")
			setPaymentDate("")
			setAddingPayment(false)
		} catch (err) {
			console.error("Error saving payment:", err)
			pushAlert({
				type: "ERROR",
				title: "Error saving payment.",
			})
		} finally {
			setSavingPayment(false)
		}
	}

	// Update voucher type
	const updateType = async () => {
		setSavingType(true)

		try {
			await db.doc(`gift_vouchers/${id}`).update({
				type: selectedType,
			})

			pushAlert({
				type: "SUCCESS",
				title: "Voucher type updated successfully.",
			})

			setEditingType(false)
		} catch (err) {
			console.error("Error updating type:", err)
			pushAlert({
				type: "ERROR",
				title: "Error updating voucher type.",
			})
		} finally {
			setSavingType(false)
		}
	}

	// Fetch and view payments
	const openViewPayments = async () => {
		setViewingPayments(true)

		try {
			const paymentDocs = await db.collection(`gift_vouchers/${id}/payments`).orderBy("paid_date", "desc").get()

			if (paymentDocs.size > 0) {
				const paymentsArr = []
				paymentDocs.forEach((paymentDoc) => {
					const { amount, paid_date, payee, reference } = paymentDoc.data()
					const paidDateReadable = moment(paid_date?.seconds, "X").format("DD/MM/YYYY")
					paymentsArr.push({ amount, paidDateReadable, payee, reference })
				})
				setPayments(paymentsArr)
			} else {
				setPayments([])
			}
		} catch (err) {
			console.error("Error fetching payments:", err)
			setPayments([])
		}
	}

	// Delete voucher
	const deleteVoucher = async () => {
		if (!window.confirm(`Are you sure you want to delete voucher ${reference}?`)) return

		setDeleting(true)

		try {
			await db.doc(`gift_vouchers/${id}`).delete()

			pushAlert({
				type: "SUCCESS",
				title: "Voucher deleted successfully.",
			})

			// Callback to refresh the list
			if (onDelete) onDelete()
		} catch (err) {
			console.error("Error deleting voucher:", err)
			pushAlert({
				type: "ERROR",
				title: "Error deleting voucher.",
			})
		} finally {
			setDeleting(false)
		}
	}

	return (
		<tr>
			<td>{purchasedString}</td>
			<td>
				{document?.pdf_url && (
					<a
						href={document?.pdf_url}
						target="_blank">
						{reference}
					</a>
				)}

				{!document?.pdf_url && <>{reference}</>}
			</td>
			<td>
				{issuedStaff?.first_name && (
					<>
						{issuedStaff.first_name} {issuedStaff.last_name}
					</>
				)}

				{!issuedStaff?.first_name && <small>Not issued internally</small>}
			</td>
			<td>
				{byClient.first_name && (
					<NavLink to={`/client/${purchased_by}`}>
						{byClient.first_name} {byClient.last_name}
					</NavLink>
				)}

				{!byClient.first_name && !note && <small>Not bought directly</small>}

				{!byClient.first_name && note && <small>{note}</small>}
			</td>
			<td>
				{purchased_for ? (
					<NavLink to={`/client/${purchased_for}`}>
						{forClient.first_name} {forClient.last_name}
					</NavLink>
				) : (
					<small>Not assigned</small>
				)}{" "}
				<span
					className="edit-link"
					onClick={() => openEditDetails()}
					style={{ color: "#dc3545", cursor: "pointer", fontSize: "12px", fontWeight: 500 }}>
					Edit
				</span>
			</td>
			<td>
				{typeLabels[type] || type || <small>Not set</small>}{" "}
				<span
					className="edit-link"
					onClick={() => {
						setSelectedType(type || "")
						setEditingType(true)
					}}
					style={{ color: "#dc3545", cursor: "pointer", fontSize: "12px", fontWeight: 500 }}>
					Edit
				</span>
			</td>
			<td>{formattedPrice}</td>
			<td>
				{status === "PENDING" && (
					<Badge
						type="NEGATIVE"
						label="Not paid"
					/>
				)}

				{status === "REDEEMED" && (
					<Badge
						type="POSITIVE"
						label="Redeemed"
					/>
				)}

				{status === "PAID" && (
					<Badge
						type="INFO"
						label="Paid & Available"
					/>
				)}
			</td>
			<td>
				{booking?.reference && <NavLink to={`/booking/${bookingID}`}>GHDB-{booking.reference}</NavLink>}
				{!booking?.reference && <small>Not used yet</small>}
			</td>
			<td className="is-button">
				<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "fit-content", marginLeft: "auto", gap: "4px" }}>
					{!document?.pdf_url && (
						<Button
							small={true}
							icon={<UploadIcon size={16} />}
							label="Generate"
							loading={generating}
							loadingText="..."
							onClick={() => generateVoucher()}
						/>
					)}

					{type === "EXT_PURCHASED" && (
						<>
							<Button
								small={true}
								label="Add Payment"
								onClick={() => setAddingPayment(true)}
							/>
							<Button
								small={true}
								icon={<Eye size={14} />}
								onClick={() => openViewPayments()}
							/>
						</>
					)}

					<Button
						small={true}
						label="Send (Buyer)"
						loading={sending}
						loadingText="Sending..."
						onClick={() => sendVoucher()}
						disabled={!document?.pdf_url || !byClient.email}
					/>

					{auth.currentUser?.uid === "CGOHCdUwFAOe13OSTY2D9qdO2LX2" && (
						<Button
							small={true}
							icon={<Trash2 size={14} />}
							loading={deleting}
							loadingText="..."
							onClick={() => deleteVoucher()}
						/>
					)}
				</div>

				{addingPayment && (
					<Window
						title="Add Payment"
						className="slim"
						close={() => setAddingPayment(false)}>
						<WindowCard>
							<div className="input-grid cols-2">
								<div className="span-2">
									<Input
										type="text"
										placeholder="Payee:"
										value={paymentPayee}
										onChange={setPaymentPayee}
									/>
								</div>

								<Input
									type="number"
									placeholder="Amount:"
									symbol="£"
									value={paymentAmount}
									onChange={setPaymentAmount}
								/>

								<Input
									type="text"
									placeholder="Reference:"
									value={paymentReference}
									onChange={setPaymentReference}
								/>

								<div className="span-2">
									<Datepicker
										placeholder="Payment date:"
										onSelect={(date) => setPaymentDate(date)}
									/>
								</div>

								<div className="span-2">
									<Button
										label="Save payment"
										fullWidth={true}
										loading={savingPayment}
										loadingText="Saving..."
										onClick={() => savePayment()}
										disabled={!paymentAmount || !paymentPayee || !paymentReference || !paymentDate}
									/>
								</div>
							</div>
						</WindowCard>
					</Window>
				)}

				{editingDetails && (
					<Window
						title="Edit Voucher Details"
						className="slim"
						close={() => setEditingDetails(false)}>
						<WindowCard>
							<div className="input-grid cols-2">
								<Input
									type="text"
									placeholder="First name:"
									value={editFirstName}
									onChange={setEditFirstName}
								/>

								<Input
									type="text"
									placeholder="Last name:"
									value={editLastName}
									onChange={setEditLastName}
								/>

								<div className="span-2">
									<Input
										type="email"
										placeholder="Email:"
										value={editEmail}
										onChange={setEditEmail}
									/>
								</div>

								<div className="span-2">
									<Button
										label="Update details"
										fullWidth={true}
										loading={savingDetails}
										loadingText="Saving..."
										onClick={() => updateVoucherDetails()}
										disabled={!editFirstName || !editLastName || !editEmail}
									/>
								</div>
							</div>
						</WindowCard>
					</Window>
				)}

				{editingType && (
					<Window
						title="Edit Voucher Type"
						className="slim"
						close={() => setEditingType(false)}>
						<WindowCard>
							<div className="input-grid cols-1">
								<Select
									placeholder="Select voucher type"
									options={{
										INT_COMPENSATION: "INT/Compensation",
										EXT_PARTNER: "EXT/Partner",
										EXT_MARKETING: "EXT/Marketing",
										EXT_PURCHASED: "EXT/Purchased",
									}}
									selected={selectedType}
									onSelect={(selected) => setSelectedType(selected.option)}
									showLabel={true}
									label="Voucher type:"
								/>

								<Button
									label="Update type"
									fullWidth={true}
									loading={savingType}
									loadingText="Saving..."
									onClick={() => updateType()}
									disabled={!selectedType}
								/>
							</div>
						</WindowCard>
					</Window>
				)}

				{viewingPayments && (
					<Window
						title="Voucher Payments"
						className="slim"
						close={() => setViewingPayments(false)}>
						{payments.length === 0 && <small style={{ display: "block", padding: "20px", color: "#666" }}>No payments recorded yet.</small>}

						{payments.length > 0 && (
							<div style={{ maxHeight: "300px", overflowY: "auto" }}>
								<table style={{ width: "100%", borderCollapse: "collapse" }}>
									<tbody>
										{payments.map((payment, index) => (
											<tr
												key={index}
												style={{ borderBottom: "1px solid #eee" }}>
												<td style={{ padding: "10px 12px", fontSize: "13px", lineHeight: "1em" }}>{payment.paidDateReadable}</td>
												<td style={{ padding: "10px 12px", fontSize: "12px", color: "#666", lineHeight: "1em" }}>{payment.reference}</td>
												<td style={{ padding: "10px 12px", fontSize: "13px", lineHeight: "1em" }}>{payment.payee}</td>
												<td style={{ padding: "10px 12px", fontSize: "13px", fontWeight: 500, lineHeight: "1em" }}>£{payment.amount?.toLocaleString()}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</Window>
				)}
			</td>
		</tr>
	)
}
